// Client for the media slots API (api/media.php) — admin-manageable
// photos/videos rendered across the guest site.

import { parseJsonResponse } from './apiFetch';
const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';
const BACKEND_ROOT: string = import.meta.env.VITE_BACKEND_ROOT ?? API_BASE.replace(/\/api\/?$/, '');

export function resolveMediaUrl(relativePath: string): string {
  if (/^https?:\/\//.test(relativePath)) return relativePath;
  return `${BACKEND_ROOT}/${relativePath.replace(/^\/+/, '')}`;
}

export interface MediaSlotRow {
  slot_key: string;
  file_path: string | null;
  media_type: 'image' | 'video' | 'map';
  original_name: string | null;
  updated_at: string | null;
  ai_enhanced: 0 | 1;
  caption: string;
}

export async function fetchMediaSlots(): Promise<Record<string, MediaSlotRow>> {
  // Cache-busted — see the matching comment in reviewPhotosApi.ts. This is
  // called exclusively from admin pages, which need to see uploads and the
  // "AI enhanced" toggle reflected immediately rather than the 60s cached
  // response media.php sends for the (separately-fetched) guest site.
  const res = await fetch(`${API_BASE}/media.php?action=list&v=${Date.now()}`);
  const data = await parseJsonResponse(res);
  if (!data.success) throw new Error(data.message ?? 'Failed to load media');
  const map: Record<string, MediaSlotRow> = {};
  (data.slots as MediaSlotRow[]).forEach((s) => {
    map[s.slot_key] = s;
  });
  return map;
}

export async function uploadMediaSlot(
  slotKey: string,
  mediaType: 'image' | 'video',
  file: File,
  csrfToken: string,
  aiEnhanced = false,
): Promise<{ success: boolean; file_path?: string; message?: string }> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('slot_key', slotKey);
  fd.append('media_type', mediaType);
  fd.append('ai_enhanced', aiEnhanced ? '1' : '0');
  fd.append('csrf_token', csrfToken);
  const res = await fetch(`${API_BASE}/media.php?action=upload`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  return parseJsonResponse(res);
}

/** Toggles the "may be AI-enhanced" disclosure flag on an existing slot
 * without touching its file. */
export async function setMediaAiEnhanced(
  slotKey: string,
  aiEnhanced: boolean,
  csrfToken: string,
): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/media.php?action=set_ai_enhanced`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slot_key: slotKey, ai_enhanced: aiEnhanced, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

/** Sets (or clears) the short caption shown as an overlay band at the
 * bottom of the photo on the guest site, without touching its file. */
export async function setMediaCaption(
  slotKey: string,
  caption: string,
  csrfToken: string,
): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/media.php?action=set_caption`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slot_key: slotKey, caption, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export interface RecompressResult {
  slot_key: string;
  status: 'compressed' | 'skipped';
  reason?: string;
  before_kb?: number;
  after_kb?: number;
  saved_pct?: number;
}

/** Re-runs the upload-time image compression pass against every existing
 * media slot — catches up images uploaded before compression existed
 * (large PNG/JPEG hero photos etc.) without an admin re-uploading each one. */
export async function recompressAllMedia(
  csrfToken: string,
): Promise<{ success: boolean; message?: string; results?: RecompressResult[]; total_before_kb?: number; total_after_kb?: number }> {
  const res = await fetch(`${API_BASE}/media.php?action=recompress_all`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export async function deleteMediaSlot(slotKey: string, csrfToken: string): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/media.php?action=delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slot_key: slotKey, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export async function setMediaLink(
  slotKey: string,
  url: string,
  csrfToken: string,
  mediaType: 'video' | 'map' = 'video',
): Promise<{ success: boolean; file_path?: string; media_type?: string; message?: string }> {
  const res = await fetch(`${API_BASE}/media.php?action=set_link`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slot_key: slotKey, url, media_type: mediaType, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

/** Recognizes YouTube/Vimeo links and returns an iframe-embeddable URL.
 * Returns null for anything else (direct .mp4/.webm/.mov links), which the
 * caller should render with a plain <video> tag instead. */
export function getVideoEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');

    // youtube-nocookie.com instead of youtube.com: avoids setting YouTube's
    // tracking cookies until the visitor actually interacts with the player.
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1);
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname === '/watch') {
        const id = u.searchParams.get('v');
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
      }
      if (u.pathname.startsWith('/embed/')) return `https://www.youtube-nocookie.com${u.pathname}${u.search}`;
      if (u.pathname.startsWith('/shorts/')) {
        const id = u.pathname.split('/')[2];
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
      }
    }
    if (host === 'vimeo.com') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    if (host === 'player.vimeo.com') return url;

    return null;
  } catch {
    return null;
  }
}
